import {
  EKycDocumentVerificationStatus,
  EKycVerificationStatus,
  useGetEKycInfoByExamineeIdAndExamEventIdQuery,
} from '@exam-center/graphql';
import { Navigate } from '@exam-center/router';
import { Loading } from '@shared/components';
import { createContext, useMemo } from 'react';

import { ACCESSIBLE_EKYC_STATUSES } from '../../constants';

import type {
  ProctoringEKycVerificationContextValue,
  ProctoringEKycVerificationProviderProps,
} from './types';

const ProctoringEKycVerificationContext = createContext<
  ProctoringEKycVerificationContextValue | undefined
>(undefined);

export default function ProctoringEKycVerificationProvider(
  props: Readonly<ProctoringEKycVerificationProviderProps>,
) {
  const { children, examCenterCode, examEventId, examineeId } = props;
  const { data, loading } = useGetEKycInfoByExamineeIdAndExamEventIdQuery({
    variables: {
      getEKycInfoByExamineeIdAndExamEventIdInput: {
        examEventId,
        examineeId,
      },
    },
    fetchPolicy: 'cache-and-network',
  });
  const examineeEKycInfo = data?.ecGetEKycInfoByExamineeIdAndExamEventId.eKycInfo;

  const contextValue: ProctoringEKycVerificationContextValue = useMemo(() => {
    return {
      eKycInfo: {
        title: examineeEKycInfo?.title ?? '',
        firstName: examineeEKycInfo?.firstname ?? '',
        middleName: examineeEKycInfo?.middleName ?? null,
        lastName: examineeEKycInfo?.lastname ?? '',
        idNumber: examineeEKycInfo?.idNumber ?? '',
        dateOfBirth: examineeEKycInfo?.dateOfBirth ?? new Date(),
        email: examineeEKycInfo?.email ?? '',
        status: examineeEKycInfo?.status ?? EKycVerificationStatus.NotStarted,
        faceMatched: examineeEKycInfo?.faceMatched ?? EKycDocumentVerificationStatus.NotStarted,
        comment: examineeEKycInfo?.comment ?? '',
        verificationCheckList: {
          photoClarity: examineeEKycInfo?.verificationCheckList?.photoClarity ?? null,
          photoIdentityMatch: examineeEKycInfo?.verificationCheckList?.photoIdentityMatch ?? null,
          idPhotoClarity: examineeEKycInfo?.verificationCheckList?.idPhotoClarity ?? null,
        },
        documents: examineeEKycInfo?.documents ?? [],
      },
      examCenterCode,
      examEventId,
      examineeId,
      loading,
    };
  }, [examCenterCode, examEventId, examineeId, examineeEKycInfo, loading]);

  if (loading && !examineeEKycInfo) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  if (examineeEKycInfo && !ACCESSIBLE_EKYC_STATUSES.includes(examineeEKycInfo.status)) {
    return (
      <Navigate
        to='/:examCenterCode/proctoring/exam-events/:examEventId/e-kyc'
        params={{ examCenterCode, examEventId }}
        replace
      />
    );
  }

  return (
    <ProctoringEKycVerificationContext.Provider value={contextValue}>
      {children}
    </ProctoringEKycVerificationContext.Provider>
  );
}

export { ProctoringEKycVerificationContext };
